import React, { FCC } from 'react'

import { Skeleton } from '@sketch/components'
import BillingInfoTable from '../BillingInfoTable'

import { formatPriceWithCurrency } from '@sketch/utils'

import {
  BillingPriceFragment,
  BillingSeatsInfoFragment,
  useDraftBillingSummaryQuery,
  useGetAvailablePlansQuery,
} from '@sketch/gql-types'

import {
  isBusinessPlan,
  isTeamMonthlyPlan,
  isTeamYearlyPlan,
} from '../../utils'
import { useGetClientCurrency } from '../../operations/useClientCurrency'

interface ProjectedTotalProps {
  seats: BillingSeatsInfoFragment
  projectedCost?: BillingPriceFragment
  hasSubscription?: boolean
}

interface PreviewPlanProjectedCostProps {
  seats: BillingSeatsInfoFragment
}

const BillingRow = ({ children }: React.PropsWithChildren<{}>) => (
  <BillingInfoTable.Row>
    <BillingInfoTable.HeadingCell>Projected Total</BillingInfoTable.HeadingCell>
    <BillingInfoTable.Cell>{children}</BillingInfoTable.Cell>
  </BillingInfoTable.Row>
)

const PreviewPlanProjectedCost: FCC<PreviewPlanProjectedCostProps> = ({
  seats,
}) => {
  const { data, loading: loadingPlans } = useGetAvailablePlansQuery()
  const { data: currencyData, loading: loadingCurrency } =
    useGetClientCurrency()

  const currency = currencyData?.currencies.default

  const businessPlan = data?.availablePlans.find(isBusinessPlan)
  const monthlyPlan = data?.availablePlans.find(isTeamMonthlyPlan)
  const yearlyPlan = data?.availablePlans.find(isTeamYearlyPlan)

  const { data: draftBillingSummaryYearlyData, loading: loadingYearly } =
    useDraftBillingSummaryQuery({
      variables: {
        planId: yearlyPlan?.id || '',
        seats: seats.currentSeatsTotal,
        currency,
      },
      skip: !currency || !yearlyPlan,
    })

  const { data: draftBillingSummaryMonthlyData, loading: loadingMonthly } =
    useDraftBillingSummaryQuery({
      variables: {
        planId: monthlyPlan?.id || '',
        seats: seats.currentSeatsTotal,
        currency,
      },
      skip: !currency || !monthlyPlan,
    })

  const { data: draftBillingSummaryBusinessData, loading: loadingBusiness } =
    useDraftBillingSummaryQuery({
      variables: {
        planId: businessPlan?.id || '',
        seats: seats.currentSeatsTotal,
        currency,
      },
      skip: !currency || !businessPlan,
    })

  const loading =
    loadingCurrency ||
    loadingPlans ||
    loadingMonthly ||
    loadingYearly ||
    loadingBusiness

  if (loading) {
    return (
      <BillingRow>
        <Skeleton width="200px" />
      </BillingRow>
    )
  }

  const billingCycleAmountYearly =
    draftBillingSummaryYearlyData?.draftBillingSummary.price
  const billingCycleAmountMonthly =
    draftBillingSummaryMonthlyData?.draftBillingSummary.price
  const billingCycleAmountBusiness =
    draftBillingSummaryBusinessData?.draftBillingSummary.price

  if (
    !billingCycleAmountYearly ||
    !billingCycleAmountMonthly ||
    !billingCycleAmountBusiness
  ) {
    return (
      <BillingRow>Something went wrong fetching the projected costs</BillingRow>
    )
  }

  return (
    <BillingRow>
      <span data-testid="draft-amount">
        <strong>
          {billingCycleAmountYearly &&
            formatPriceWithCurrency(
              billingCycleAmountYearly.invoice,
              billingCycleAmountYearly.currency
            )}
        </strong>{' '}
        yearly or{' '}
        <strong>
          {billingCycleAmountMonthly &&
            formatPriceWithCurrency(
              billingCycleAmountMonthly.invoice,
              billingCycleAmountMonthly.currency
            )}
        </strong>{' '}
        monthly for the Standard plan or{' '}
        <strong>
          {billingCycleAmountBusiness &&
            formatPriceWithCurrency(
              billingCycleAmountBusiness.invoice,
              billingCycleAmountBusiness.currency
            )}
        </strong>{' '}
        yearly for the Professional plan
        <br />
        With a paid subscription you would be charged for the total number of
        Editor Seats you’re using. Total excluding tax.
      </span>
    </BillingRow>
  )
}

const ProjectedTotal = (props: ProjectedTotalProps) => {
  const { hasSubscription, projectedCost, seats } = props

  if (hasSubscription && projectedCost) {
    return (
      <BillingRow>
        <div>
          <strong data-testid="projected-total-amount">
            {formatPriceWithCurrency(
              projectedCost.invoice,
              projectedCost.currency
            )}
          </strong>
          <br />
          Total excluding tax
        </div>
      </BillingRow>
    )
  }

  return <PreviewPlanProjectedCost seats={seats} />
}

export default ProjectedTotal
